import React, { useState } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useParams, Link } from 'react-router-dom';
import {
  MY_COMPANY_QUERY,
  UPDATE_COMPANY_BRANDING_MUTATION,
  ADD_COMPANY_ADMIN_MUTATION,
  SEARCH_PLATFORM_USERS_QUERY,
} from '../graphql';
import { useAuth } from '../context/AuthContext';

const TABS = ['Overview', 'Admins', 'Branding'];

export default function CompanyDashboard() {
  const { slug } = useParams();
  const { currentUser } = useAuth();
  const [tab, setTab] = useState('Overview');

  // Branding form state
  const [brandingForm, setBrandingForm] = useState(null); // lazy-init from query data
  const [brandingError, setBrandingError] = useState('');
  const [brandingSaved, setBrandingSaved] = useState(false);

  // Admins tab state
  const [adminSearch, setAdminSearch] = useState('');
  const [addedAdmins, setAddedAdmins] = useState([]);
  const [adminError, setAdminError] = useState('');

  const { data, loading, error, refetch } = useQuery(MY_COMPANY_QUERY, {
    onCompleted: (d) => {
      const c = d?.myCompany;
      if (c && !brandingForm) {
        setBrandingForm({
          logoUrl: c.logoUrl || '',
          brandColor: c.brandColor || '#C05621',
          tagline: c.tagline || '',
          website: c.website || '',
        });
      }
    },
  });

  const [updateBranding, { loading: savingBranding }] = useMutation(UPDATE_COMPANY_BRANDING_MUTATION, {
    onCompleted: () => {
      setBrandingSaved(true);
      setTimeout(() => setBrandingSaved(false), 3000);
      refetch();
    },
    onError: (e) => setBrandingError(e.message),
  });

  const [addAdmin, { loading: addingAdmin }] = useMutation(ADD_COMPANY_ADMIN_MUTATION, {
    onCompleted: () => refetch(),
    onError: (e) => setAdminError(e.message),
  });

  const [searchUsers, { data: searchData, loading: searching }] = useLazyQuery(
    SEARCH_PLATFORM_USERS_QUERY,
    { fetchPolicy: 'network-only' }
  );

  if (!currentUser) {
    return (
      <div className="page container" style={{ paddingTop: 80 }}>
        <p>Please sign in to view your company dashboard.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <div style={{ color: 'var(--text-muted)' }}>Loading company…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page container" style={{ paddingTop: 80 }}>
        <div style={{ color: 'var(--danger)', padding: '12px 16px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)' }}>
          Error: {error.message}
        </div>
      </div>
    );
  }

  const company = data?.myCompany;
  if (!company) {
    return (
      <div className="page container" style={{ paddingTop: 80 }}>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏢</div>
          <h2 style={{ fontSize: 'var(--font-lg)', fontWeight: 700, marginBottom: 8 }}>No company yet</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
            You haven't set up a company space yet.
          </p>
          <Link to="/company/register" className="btn btn-primary">
            Create Company Space
          </Link>
        </div>
      </div>
    );
  }

  const bf = brandingForm || {
    logoUrl: company.logoUrl || '',
    brandColor: company.brandColor || '#C05621',
    tagline: company.tagline || '',
    website: company.website || '',
  };

  const setBF = (k) => (e) => setBrandingForm(f => ({ ...f, [k]: e.target.value }));

  const handleSaveBranding = (e) => {
    e.preventDefault();
    setBrandingError('');
    updateBranding({
      variables: {
        companyId: company.id,
        logoUrl: bf.logoUrl.trim() || null,
        brandColor: bf.brandColor,
        tagline: bf.tagline.trim() || null,
        website: bf.website.trim() || null,
      },
    });
  };

  const handleAddAdmin = async (userId) => {
    setAdminError('');
    try {
      await addAdmin({ variables: { companyId: company.id, userId } });
      setAddedAdmins(prev => [...prev, userId]);
    } catch (_) {}
  };

  const searchResults = searchData?.searchPlatformUsers ?? [];
  const existingAdminIds = new Set((company.admins || []).map(a => a.id));

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: 700, paddingTop: 32, paddingBottom: 48 }}>
        {/* Company banner */}
        <div style={{
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
          marginBottom: 28,
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow)',
        }}>
          <div style={{
            background: company.brandColor || 'var(--primary)',
            padding: '24px 28px',
            display: 'flex',
            alignItems: 'center',
            gap: 18,
          }}>
            {company.logoUrl ? (
              <img
                src={company.logoUrl}
                alt={company.name}
                style={{ width: 56, height: 56, objectFit: 'contain', background: 'rgba(255,255,255,0.9)', borderRadius: 10, padding: 4 }}
              />
            ) : (
              <div style={{
                width: 56, height: 56, borderRadius: 10,
                background: 'rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 28, color: '#fff',
              }}>
                {company.name[0].toUpperCase()}
              </div>
            )}
            <div>
              <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 800, color: '#fff', margin: 0 }}>
                {company.name}
              </h1>
              {company.tagline && (
                <div style={{ fontSize: 'var(--font-sm)', color: 'rgba(255,255,255,0.85)', marginTop: 4 }}>
                  {company.tagline}
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                <a
                  href={`https://${company.slug}.ledxo.com`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', fontFamily: 'monospace' }}
                >
                  {company.slug}.ledxo.com ↗
                </a>
                {company.industry && (
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{company.industry}</span>
                )}
              </div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <Link
                to={`/company/${company.slug}`}
                style={{
                  padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                  background: 'rgba(255,255,255,0.2)', color: '#fff',
                  fontSize: 'var(--font-sm)', fontWeight: 600,
                  border: '1px solid rgba(255,255,255,0.35)',
                  textDecoration: 'none',
                }}
              >
                View public page ↗
              </Link>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid var(--border)' }}>
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '10px 18px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 'var(--font-sm)',
                color: tab === t ? 'var(--primary)' : 'var(--text-muted)',
                borderBottom: tab === t ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: -2,
                transition: 'all 0.15s',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* ── TAB: Overview ── */}
        {tab === 'Overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div className="card" style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--primary)' }}>
                {(company.admins || []).length}
              </div>
              <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginTop: 4 }}>Admins</div>
            </div>
            <div className="card" style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--primary)' }}>
                {company.sizeBand || '—'}
              </div>
              <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginTop: 4 }}>Company Size</div>
            </div>
            <div className="card" style={{ padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--primary)' }}>
                {company.plan || 'Free'}
              </div>
              <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginTop: 4 }}>Plan</div>
            </div>

            {/* Company details card */}
            <div className="card" style={{ padding: 20, gridColumn: '1 / -1' }}>
              <h3 style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 14 }}>Company Details</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                {company.description && (
                  <div style={{ gridColumn: '1 / -1', fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginBottom: 4 }}>
                    {company.description}
                  </div>
                )}
                {company.industry && (
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 'var(--font-sm)' }}>Industry: </span>
                    <span style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)' }}>{company.industry}</span>
                  </div>
                )}
                {company.website && (
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 'var(--font-sm)' }}>Website: </span>
                    <a href={company.website} target="_blank" rel="noreferrer" style={{ fontSize: 'var(--font-sm)', color: 'var(--primary)' }}>
                      {company.website}
                    </a>
                  </div>
                )}
                <div>
                  <span style={{ fontWeight: 600, fontSize: 'var(--font-sm)' }}>Created: </span>
                  <span style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)' }}>
                    {new Date(company.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 'var(--font-sm)' }}>Public URL: </span>
                  <Link to={`/company/${company.slug}`} style={{ fontSize: 'var(--font-sm)', color: 'var(--primary)' }}>
                    /company/{company.slug}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Admins ── */}
        {tab === 'Admins' && (
          <div>
            <h3 style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 16 }}>
              Current Admins
            </h3>

            {(company.admins || []).length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)', marginBottom: 20 }}>
                No admins added yet.
              </div>
            ) : (
              <div className="card" style={{ marginBottom: 24, overflow: 'hidden' }}>
                {(company.admins || []).map((admin, i) => (
                  <div key={admin.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    borderBottom: i < company.admins.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <div className="avatar avatar-sm" style={{ background: admin.avatarColor || 'var(--primary)', flexShrink: 0 }}>
                      {(admin.name || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 'var(--font-sm)' }}>{admin.name}</div>
                      {admin.email && (
                        <div style={{ fontSize: 'calc(var(--font-sm) - 1px)', color: 'var(--text-muted)' }}>{admin.email}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <h3 style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 12 }}>
              Add New Admin
            </h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                className="input"
                style={{ flex: 1 }}
                placeholder="Search by name or email…"
                value={adminSearch}
                onChange={e => setAdminSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && adminSearch.trim()) {
                    searchUsers({ variables: { search: adminSearch.trim(), groupId: company.id } });
                  }
                }}
              />
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (adminSearch.trim()) {
                    searchUsers({ variables: { search: adminSearch.trim(), groupId: company.id } });
                  }
                }}
                disabled={searching}
              >
                {searching ? '…' : 'Search'}
              </button>
            </div>

            {adminError && (
              <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 12 }}>
                {adminError}
              </div>
            )}

            {searchResults.length > 0 && (
              <div className="card" style={{ overflow: 'hidden' }}>
                {searchResults.map((u, i) => {
                  const isExistingAdmin = existingAdminIds.has(u.id);
                  const justAdded = addedAdmins.includes(u.id);
                  return (
                    <div key={u.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                      borderBottom: i < searchResults.length - 1 ? '1px solid var(--border)' : 'none',
                    }}>
                      <div className="avatar avatar-sm" style={{ background: u.avatarColor || 'var(--primary)', flexShrink: 0 }}>
                        {(u.name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 'var(--font-sm)' }}>{u.name}</div>
                        <div style={{ fontSize: 'calc(var(--font-sm) - 1px)', color: 'var(--text-muted)' }}>{u.email}</div>
                      </div>
                      {isExistingAdmin || justAdded ? (
                        <span style={{ fontSize: 'var(--font-sm)', color: 'var(--success)', fontWeight: 600 }}>✓ Admin</span>
                      ) : (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleAddAdmin(u.id)}
                          disabled={addingAdmin}
                          style={{ border: '1px solid var(--primary)', color: 'var(--primary)' }}
                        >
                          + Add admin
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Branding ── */}
        {tab === 'Branding' && brandingForm && (
          <div>
            {/* Live preview */}
            <div style={{
              borderRadius: 'var(--radius)',
              overflow: 'hidden',
              marginBottom: 24,
              border: '1px solid var(--border)',
            }}>
              <div style={{
                background: bf.brandColor,
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}>
                {bf.logoUrl ? (
                  <img src={bf.logoUrl} alt="" style={{ width: 44, height: 44, objectFit: 'contain', background: 'rgba(255,255,255,0.9)', borderRadius: 8, padding: 3 }} onError={() => {}} />
                ) : (
                  <div style={{
                    width: 44, height: 44, borderRadius: 8,
                    background: 'rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 22, color: '#fff',
                  }}>
                    {company.name[0].toUpperCase()}
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: '#fff' }}>{company.name}</div>
                  {bf.tagline && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>{bf.tagline}</div>}
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{company.slug}.ledxo.com</div>
                </div>
              </div>
              <div style={{ background: 'var(--surface2)', padding: '6px 20px', fontSize: 11, color: 'var(--text-muted)' }}>
                Live preview
              </div>
            </div>

            <form onSubmit={handleSaveBranding}>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0 16px', marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                    Brand Color
                  </label>
                  <input
                    type="color"
                    value={bf.brandColor}
                    onChange={setBF('brandColor')}
                    style={{ width: '100%', height: 42, padding: 4, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                    Logo URL
                  </label>
                  <input
                    className="input"
                    type="url"
                    value={bf.logoUrl}
                    onChange={setBF('logoUrl')}
                    placeholder="https://cdn.yourcompany.com/logo.png"
                  />
                </div>
              </div>

              {bf.logoUrl && (
                <div style={{ marginBottom: 16 }}>
                  <img
                    src={bf.logoUrl}
                    alt="Logo preview"
                    style={{ height: 48, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 4 }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                  Tagline <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(max 80 chars)</span>
                </label>
                <input
                  className="input"
                  value={bf.tagline}
                  maxLength={80}
                  onChange={setBF('tagline')}
                  placeholder="Connect, grow and thrive together."
                />
                <p style={{ fontSize: 'calc(var(--font-sm) - 1px)', color: 'var(--text-muted)', marginTop: 4 }}>
                  {bf.tagline.length}/80
                </p>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 'var(--font-sm)', marginBottom: 6 }}>
                  Website URL
                </label>
                <input
                  className="input"
                  type="url"
                  value={bf.website}
                  onChange={setBF('website')}
                  placeholder="https://yourcompany.com"
                />
              </div>

              {brandingError && (
                <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 14 }}>
                  {brandingError}
                </div>
              )}

              {brandingSaved && (
                <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-sm)', background: 'rgba(56,120,90,0.1)', color: 'var(--success)', fontSize: 'var(--font-sm)', marginBottom: 14 }}>
                  ✓ Branding saved!
                </div>
              )}

              <button type="submit" className="btn btn-primary" disabled={savingBranding}>
                {savingBranding ? 'Saving…' : 'Save Branding'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
